import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  DollarSign, TrendingDown, FileText,
  Download,
  Banknote, TrendingUp as TrendingUpIcon, CreditCard
} from 'lucide-react';

import ReportLayout from '../components/ReportLayout';
import { Link } from 'react-router-dom';

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-KE', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const formatMonth = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-KE', { month: 'short', year: 'numeric' });
  } catch {
    return value;
  }
};

const currency = (v) => `KES ${Number(v || 0).toLocaleString()}`;
const shortCurrency = (v) => `KES ${Number(v || 0).toLocaleString()}`;

const pctChange = (current, previous) => {
  const c = Number(current || 0);
  const p = Number(previous || 0);
  if (p === 0) return null;
  return ((c - p) / p) * 100;
};

const ProfitLossComparisonTable = ({ plRows = [] }) => {
  const sorted = [...plRows].sort((a, b) => new Date(a.month) - new Date(b.month));
  const thisYear = sorted[sorted.length - 1] || null;
  const lastYear = sorted.length >= 2 ? sorted[sorted.length - 2] : null;

  const lastYearRow = (() => {
    if (sorted.length >= 12) {
      return sorted[sorted.length - 13] || null;
    }
    return null;
  })();

  const compRow = lastYearRow || lastYear;

  const rows = [
    { label: 'Total Revenue', key: 'total_revenue', positive: true },
    { label: 'Cost of Goods Sold', key: 'cogs', fallbackKey: 'cost_of_goods_sold', negative: true },
    { label: 'Gross Profit', key: 'gross_profit', positive: true, bold: true },
    { label: 'Operating Expenses', key: 'operating_expenses', fallbackKey: 'operating_expense', negative: true },
    { label: 'Operating Profit', key: 'operating_profit', positive: true },
    { label: 'Net Profit', key: 'net_profit', positive: true, netProfit: true },
  ];

  const getVal = (row, r) => {
    if (!row) return 0;
    if (r.key in row) return Number(row[r.key] || 0);
    if (r.fallbackKey && r.fallbackKey in row) return Number(row[r.fallbackKey] || 0);
    return 0;
  };

  const formatPct = (v) => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    const sign = v > 0 ? '+' : '';
    return `${sign}${v.toFixed(1)}%`;
  };

  return (
    <div>
      <table className="w-full">
        <thead>
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">Description</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">This Year (2024)</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">Last Year (2023)</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">% Change</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#F3F4F6]">
          {rows.map((r) => {
            const cur = getVal(thisYear, r);
            const prev = getVal(compRow, r);
            const pct = pctChange(cur, prev);
            const isNetProfitRow = r.netProfit;

            return (
              <tr key={r.label} className="hover:bg-[#F9FAFB]">
                <td className={`px-4 py-3.5 text-sm ${isNetProfitRow ? 'font-bold text-[#2563EB]' : 'font-medium text-[#1F2937]'}`}>
                  {r.label}
                </td>
                <td className={`px-4 py-3.5 text-sm text-right font-medium ${isNetProfitRow ? 'text-[#2563EB] font-bold' : 'text-[#1F2937]'}`}>
                  {currency(cur)}
                </td>
                <td className={`px-4 py-3.5 text-sm text-right font-medium ${isNetProfitRow ? 'text-[#2563EB] font-bold' : 'text-[#1F2937]'}`}>
                  {currency(prev)}
                </td>
                <td className={`px-4 py-3.5 text-sm text-right font-semibold ${
                  pct === null ? 'text-gray-500' :
                  pct >= 0 ? 'text-[#16A34A]' : 'text-[#EF4444]'
                }`}>
                  {formatPct(pct)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const escapeCsv = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const toCsv = (rows, headers) => {
  const headerLine = headers.map((h) => escapeCsv(h.label)).join(',');
  const dataLines = rows.map((r) => headers.map((h) => escapeCsv(h.value(r))).join(',')).join('\n');
  return `${headerLine}\n${dataLines}`;
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const CashFlowChart = ({
  inflowSeries = [],
  outflowSeries = [],
  netSeries = [],
  height = 280,
}) => {
  const labels = inflowSeries.map((s) => s.label);
  const n = Math.max(1, labels.length);

  const inflow = inflowSeries.map((s) => Number(s.value || 0));
  const outflow = outflowSeries.map((s) => Number(s.value || 0));
  const net = netSeries.map((s) => Number(s.value || 0));

  const maxAbs = Math.max(1, ...inflow, ...outflow, ...net.map((v) => Math.abs(v)));

  const yTop = 20;
  const yBottom = 240;
  const yZero = 140;
  const usableAbove = yZero - yTop;
  const usableBelow = yBottom - yZero;

  const valueToY = (val) => {
    if (val >= 0) {
      const ratio = clamp(Number(val) / maxAbs, 0, 1);
      return yZero - ratio * usableAbove;
    }
    const ratio = clamp(Math.abs(Number(val)) / maxAbs, 0, 1);
    return yZero + ratio * usableBelow;
  };

  const barGroupWidth = 80;
  const chartWidth = Math.max(400, n * barGroupWidth);
  const leftPad = 32;
  const rightPad = 18;
  const innerWidth = chartWidth - leftPad - rightPad;
  const groupGap = 8;
  const groupWidth = innerWidth / n;
  const barW = Math.max(8, (groupWidth - groupGap) / 3);

  const xForGroup = (i) => leftPad + i * groupWidth;

  const tickCount = 4;
  const ticks = Array.from({ length: tickCount + 1 }).map((_, i) => {
    const t = i / tickCount;
    const val = (1 - t) * maxAbs;
    return { val, y: valueToY(val) };
  });

  return (
    <div className="w-full">
      <div className="flex items-center gap-5 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-[#22C55E] inline-block rounded-sm" />
          <span>Inflow</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-[#EF4444] inline-block rounded-sm" />
          <span>Outflow</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-[#2563EB] inline-block rounded-sm" />
          <span>Net Flow</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${chartWidth} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: `${height}px` }}>
          <line x1={leftPad} x2={chartWidth - rightPad} y1={yZero} y2={yZero} stroke="#E5E7EB" strokeWidth="1" />

          {ticks.map((t, idx) => (
            <g key={idx}>
              <line x1={leftPad} x2={chartWidth - rightPad} y1={t.y} y2={t.y} stroke="#F3F4F6" strokeWidth="1" strokeDasharray="4,4" />
              {idx < ticks.length - 1 ? (
                <text x={leftPad - 8} y={t.y + 3} textAnchor="end" fontSize="9" fill="#9CA3AF">
                  {shortCurrency(t.val).replace('KES ', '')}
                </text>
              ) : null}
            </g>
          ))}

          {labels.map((label, i) => {
            const gx = xForGroup(i);
            const vIn = inflow[i] ?? 0;
            const vOut = outflow[i] ?? 0;
            const vNet = net[i] ?? 0;
            const yIn = valueToY(vIn);
            const yOut = valueToY(-vOut);
            const yNet = valueToY(vNet);
            const hIn = Math.max(2, yZero - yIn);
            const hOut = Math.max(2, yOut - yZero);
            const hNet = Math.max(2, Math.abs(yNet - yZero));

            return (
              <g key={label}>
                <rect x={gx + 4} y={yZero - hIn} width={barW} height={hIn} fill="#22C55E" rx="2" />
                <rect x={gx + 4 + barW + 4} y={yZero} width={barW} height={hOut} fill="#EF4444" rx="2" />
                {vNet >= 0 ? (
                  <rect x={gx + 4 + 2 * (barW + 4)} y={yZero - hNet} width={barW} height={hNet} fill="#2563EB" rx="2" />
                ) : (
                  <rect x={gx + 4 + 2 * (barW + 4)} y={yZero} width={barW} height={hNet} fill="#2563EB" rx="2" />
                )}
                <text x={gx + groupWidth / 2} y={height - 8} textAnchor="middle" fontSize="9" fill="#9CA3AF">
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const DonutChart = ({ slices = [], showLegend = false }) => {
  const total = slices.reduce((s, x) => s + Number(x.total || 0), 0) || 1;
  const colors = ['#2563EB', '#F97316', '#374151', '#7C3AED', '#60A5FA', '#9CA3AF', '#10B981'];

  let acc = 0;
  const radius = 45;
  const circumference = 2 * Math.PI * radius;

  const toAngle = (frac) => frac * 360 - 90;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const labelPos = (frac, midFrac) => {
    const midAngle = toAngle(frac - midFrac / 2);
    const rad = toRad(midAngle);
    const labelRadius = radius + 16;
    return {
      x: 60 + labelRadius * Math.cos(rad),
      y: 60 + labelRadius * Math.sin(rad),
    };
  };

  return (
    <div className="flex items-center justify-center">
      <svg width="240" height="200" viewBox="0 0 160 140">
        <circle cx="60" cy="70" r={radius} stroke="#E5E7EB" strokeWidth="10" fill="none" />
        {slices.map((s, i) => {
          const value = Number(s.total || 0);
          const pct = value / total;
          const dash = pct * circumference;
          const gap = circumference - dash;
          const strokeDasharray = `${dash} ${gap}`;
          const strokeDashoffset = -acc * circumference;
          const midFrac = pct;
          const pos = labelPos(acc + pct, midFrac);
          const labelPct = (pct * 100).toFixed(1);
          const showLabel = pct > 0.05;
          acc += pct;

          return (
            <g key={s.category || i}>
              <circle
                cx="60"
                cy="70"
                r={radius}
                fill="none"
                stroke={colors[i % colors.length]}
                strokeWidth="10"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                transform="rotate(-90 60 70)"
              />
              {showLabel && (
                <>
                  <text
                    x={pos.x}
                    y={pos.y - 4}
                    textAnchor="middle"
                    fontSize="7"
                    fill={colors[i % colors.length]}
                    fontWeight="600"
                  >
                    {s.category}
                  </text>
                  <text
                    x={pos.x}
                    y={pos.y + 8}
                    textAnchor="middle"
                    fontSize="7"
                    fill="#6B7280"
                  >
                    {labelPct}%
                  </text>
                </>
              )}
            </g>
          );
        })}
        <text x="60" y="66" textAnchor="middle" dominantBaseline="central" fontSize="8" fill="#6B7280">
          Total
        </text>
        <text x="60" y="80" textAnchor="middle" fontSize="10" fontWeight="700" fill="#1F2937">
          {shortCurrency(total)}
        </text>
      </svg>
    </div>
  );
};

const ExpenseBreakdownList = ({ expenseBreakdown = [] }) => {
  const colors = ['#2563EB', '#F97316', '#374151', '#7C3AED', '#60A5FA', '#9CA3AF', '#10B981'];
  const total = expenseBreakdown.reduce((s, r) => s + Number(r.total || 0), 0) || 1;

  return (
    <div className="space-y-2.5">
      {expenseBreakdown.map((item, idx) => {
        const pct = ((Number(item.total || 0) / total) * 100).toFixed(1);
        return (
          <div key={item.category || idx} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[idx % colors.length] }} />
              <span className="text-[#1F2937]">{item.category}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-semibold text-[#1F2937]">{currency(item.total)}</span>
              <span className="text-[#6B7280] w-10 text-right">{pct}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ReportsFinancial = () => {
  const getCurrentMonthRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const toISODate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return { start: toISODate(start), end: toISODate(end) };
  }, []);

  const [dateRange, setDateRange] = useState({ start: getCurrentMonthRange.start, end: getCurrentMonthRange.end });

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [errorOnce, setErrorOnce] = useState(false);

  const fetchFinancialReport = async () => {
    try {
      setLoading(true);
      const monthlyRes = await api.get('/financial/summary/monthly', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      const plRes = await api.get('/financial/summary/profit-loss', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      const cashRes = await api.get('/financial/summary/cash-flow', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      const ebRes = await api.get('/financial/summary/expense-breakdown', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      const bsRes = await api.get('/financial/summary/balance-sheet', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      const mpRes = await api.get('/financial/summary/monthly-performance', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });

      setPayload({
        profit_loss: plRes.data || [],
        revenue_vs_expenses_monthly: monthlyRes.data || [],
        cash_flow_daily: cashRes.data || [],
        expense_breakdown: ebRes.data || [],
        balance_sheet: bsRes.data || { cash_and_bank: 0, liabilities: 0, equity: 0 },
        monthly_financial_performance: mpRes.data || [],
      });
    } catch (e) {
      if (!errorOnce) {
        toast.error('Failed to load financial report');
        setErrorOnce(true);
      }
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  const plRows = payload?.profit_loss || [];
  const monthlyPerf = payload?.monthly_financial_performance || [];
  const cashFlow = useMemo(() => payload?.cash_flow_daily || [], [payload]);
  const expenseBreakdown = payload?.expense_breakdown || [];
  const balanceSheet = payload?.balance_sheet || { cash_and_bank: 0, liabilities: 0, equity: 0 };

  const totalRevenue = plRows.reduce((s, r) => s + Number(r.total_revenue || 0), 0);
  const totalGrossProfit = plRows.reduce((s, r) => s + Number(r.gross_profit || 0), 0);
  const totalNetProfit = plRows.reduce((s, r) => s + Number(r.net_profit || 0), 0);
  const totalExpenses = plRows.reduce((s, r) => s + Number(r.total_expenses || 0), 0);

  const calcGrowth = (cur, prev) => {
    const c = Number(cur || 0);
    const p = Number(prev || 0);
    if (p === 0) return null;
    return ((c - p) / p) * 100;
  };

  const revenueGrowth = plRows.length >= 2 ? calcGrowth(
    plRows[plRows.length - 1]?.total_revenue,
    plRows[plRows.length - 2]?.total_revenue
  ) : null;

  const grossProfitGrowth = plRows.length >= 2 ? calcGrowth(
    plRows[plRows.length - 1]?.gross_profit,
    plRows[plRows.length - 2]?.gross_profit
  ) : null;

  const netProfitGrowth = plRows.length >= 2 ? calcGrowth(
    plRows[plRows.length - 1]?.net_profit,
    plRows[plRows.length - 2]?.net_profit
  ) : null;

  const expenseGrowth = plRows.length >= 2 ? calcGrowth(
    plRows[plRows.length - 1]?.total_expenses,
    plRows[plRows.length - 2]?.total_expenses
  ) : null;

  const formatKpiFooter = (value, label = 'vs Last Month') => {
    if (value === null || value === undefined) return null;
    if (value > 0) return `↑ ${value.toFixed(1)}%  ${label}`;
    if (value < 0) return `↓ ${Math.abs(value).toFixed(1)}%  ${label}`;
    return `0.0%  ${label}`;
  };

  const topKpis = [
    {
      label: 'Total Revenue',
      value: currency(totalRevenue),
      icon: <DollarSign size={20} />,
      iconBg: 'bg-[#EFF6FF]',
      iconColor: 'text-[#2563EB]',
      foot: formatKpiFooter(revenueGrowth, 'vs Last Month'),
    },
    {
      label: 'Gross Profit',
      value: currency(totalGrossProfit),
      icon: <Banknote size={20} />,
      iconBg: 'bg-[#ECFDF5]',
      iconColor: 'text-[#22C55E]',
      foot: formatKpiFooter(grossProfitGrowth, 'vs Last Month'),
    },
    {
      label: 'Net Profit',
      value: currency(totalNetProfit),
      icon: <TrendingUpIcon size={20} />,
      iconBg: 'bg-[#F5F3FF]',
      iconColor: 'text-[#8B5CF6]',
      foot: formatKpiFooter(netProfitGrowth, 'vs Last Month'),
    },
    {
      label: 'Total Expenses',
      value: currency(totalExpenses),
      icon: <TrendingDown size={20} />,
      iconBg: 'bg-[#FFF7ED]',
      iconColor: 'text-[#F97316]',
      foot: formatKpiFooter(expenseGrowth, 'vs Last Month'),
    },
    {
      label: 'Cash & Bank Balance',
      value: currency(balanceSheet.cash_and_bank || 0),
      icon: <CreditCard size={20} />,
      iconBg: 'bg-[#ECFEFF]',
      iconColor: 'text-[#06B6D4]',
      foot: null,
    },
  ];

  const exportStatementsCsv = async (kind) => {
    try {
      const res = await api.get('/financial/download/statements', {
        params: { startDate: dateRange.start, endDate: dateRange.end },
      });
      const data = res.data || {};

      if (kind === 'profit_loss') {
        const headers = [
          { label: 'Month', value: (r) => r.month },
          { label: 'Total Revenue', value: (r) => r.total_revenue },
          { label: 'Gross Profit', value: (r) => r.gross_profit },
          { label: 'Total Expenses', value: (r) => r.total_expenses },
          { label: 'Net Profit', value: (r) => r.net_profit },
        ];
        const csv = toCsv(data.profit_loss || [], headers);
        downloadCsv(`financial_profit_loss_${dateRange.start}_to_${dateRange.end}.csv`, csv);
        return;
      }
      if (kind === 'monthly_performance') {
        const headers = [
          { label: 'Month', value: (r) => r.month },
          { label: 'Total Revenue', value: (r) => r.total_revenue },
          { label: 'Gross Profit', value: (r) => r.gross_profit },
          { label: 'Total Expenses', value: (r) => r.total_expenses },
          { label: 'Net Profit', value: (r) => r.net_profit },
        ];
        const csv = toCsv(data.monthly_financial_performance || [], headers);
        downloadCsv(`financial_monthly_performance_${dateRange.start}_to_${dateRange.end}.csv`, csv);
        return;
      }
      if (kind === 'revenue_vs_expenses') {
        const headers = [
          { label: 'Month', value: (r) => r.month },
          { label: 'Revenue', value: (r) => r.revenue },
          { label: 'Expenses', value: (r) => r.expenses },
          { label: 'Net Profit', value: (r) => r.net_profit },
        ];
        const csv = toCsv(data.revenue_vs_expenses_monthly || [], headers);
        downloadCsv(`financial_revenue_vs_expenses_${dateRange.start}_to_${dateRange.end}.csv`, csv);
        return;
      }
      if (kind === 'cash_flow') {
        const headers = [
          { label: 'Date', value: (r) => r.date },
          { label: 'Cash In', value: (r) => r.cash_in },
          { label: 'Cash Out', value: (r) => r.cash_out },
          { label: 'Net Cash', value: (r) => r.net_cash },
        ];
        const csv = toCsv(data.cash_flow_daily || [], headers);
        downloadCsv(`financial_cash_flow_${dateRange.start}_to_${dateRange.end}.csv`, csv);
        return;
      }
      if (kind === 'expense_breakdown') {
        const headers = [
          { label: 'Category', value: (r) => r.category },
          { label: 'Total', value: (r) => r.total },
        ];
        const csv = toCsv(data.expense_breakdown || [], headers);
        downloadCsv(`financial_expense_breakdown_${dateRange.start}_to_${dateRange.end}.csv`, csv);
        return;
      }
      if (kind === 'balance_sheet') {
        const bs = data.balance_sheet || {};
        const csv = `Category,Amount\nCash & Bank Balance,${bs.cash_and_bank || 0}\nLiabilities,${bs.liabilities || 0}\nEquity,${bs.equity || 0}`;
        downloadCsv(`financial_balance_sheet_${dateRange.start}_to_${dateRange.end}.csv`, csv);
      }
    } catch (e) {
      toast.error('Failed to download statements');
    }
  };

  // Cash flow data prepared for monthly view
  const cashFlowByMonth = useMemo(() => {
    const monthly = {};
    cashFlow.forEach((r) => {
      if (!r.date) return;
      const monthKey = r.date.substring(0, 7);
      if (!monthly[monthKey]) monthly[monthKey] = { inflow: 0, outflow: 0, net: 0 };
      monthly[monthKey].inflow += Number(r.cash_in || 0);
      monthly[monthKey].outflow += Number(r.cash_out || 0);
      monthly[monthKey].net += Number(r.net_cash || 0);
    });
    const sorted = Object.entries(monthly).sort(([a], [b]) => a.localeCompare(b));
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return {
      inflow: sorted.map(([key, val]) => ({ label: months[parseInt(key.split('-')[1]) - 1] || key, value: val.inflow })),
      outflow: sorted.map(([key, val]) => ({ label: months[parseInt(key.split('-')[1]) - 1] || key, value: val.outflow })),
      net: sorted.map(([key, val]) => ({ label: months[parseInt(key.split('-')[1]) - 1] || key, value: val.net })),
    };
  }, [cashFlow]);

  return (
    <ReportLayout
      title="Financial Report"
      description="Comprehensive overview of the company's financial performance"
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      onExportCsv={() => exportStatementsCsv('profit_loss')}
      onPrint={() => window.print()}
      printLink="/reports/financial/print"
      kpis={topKpis}
      leftSlot={
        <div className="space-y-6">
          {/* Profit & Loss Summary */}
          <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
            <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Profit & Loss Summary</h2>
            {loading ? (
              <div className="h-32 flex items-center justify-center text-gray-500 text-sm">Loading…</div>
            ) : plRows.length ? (
              <ProfitLossComparisonTable plRows={plRows} />
            ) : (
              <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No data for the selected period.</div>
            )}
          </div>

          {/* Cash Flow Overview */}
          <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#1F2937]">Cash Flow Overview</h2>
              <select className="px-3 py-1.5 border border-[#E5E7EB] rounded-lg text-sm bg-white text-gray-600">
                <option>Monthly</option>
              </select>
            </div>
            {loading ? (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
            ) : cashFlowByMonth.inflow.length > 0 ? (
              <CashFlowChart
                inflowSeries={cashFlowByMonth.inflow}
                outflowSeries={cashFlowByMonth.outflow}
                netSeries={cashFlowByMonth.net}
              />
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">No cash flow data for selected period.</div>
            )}
          </div>
        </div>
      }
      rightSlot={
        <div className="space-y-6">
          {/* Balance Sheet Summary */}
          <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
            <h2 className="text-lg font-semibold text-[#1F2937] mb-1">Balance Sheet Summary</h2>
            <p className="text-sm text-[#6B7280] mb-5">As at {formatDate(dateRange.end)}</p>
            {loading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-3">Assets</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1F2937]">Current Assets</span>
                      <span className="font-semibold text-[#2563EB]">{currency(balanceSheet.cash_and_bank || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1F2937]">Non Current Assets</span>
                      <span className="font-semibold text-[#2563EB]">KES 0</span>
                    </div>
                    <div className="flex items-center justify-between text-sm font-bold border-t border-[#F3F4F6] pt-3">
                      <span className="text-[#1F2937]">Total Assets</span>
                      <span className="text-[#2563EB]">{currency(balanceSheet.cash_and_bank || 0)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-3">Liabilities & Equity</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1F2937]">Liabilities</span>
                      <span className="font-semibold text-[#EF4444]">{currency(balanceSheet.liabilities || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1F2937]">Equity</span>
                      <span className="font-semibold text-[#EF4444]">{currency(balanceSheet.equity || 0)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm font-bold border-t border-[#F3F4F6] pt-3">
                      <span className="text-[#1F2937]">Total Liabilities & Equity</span>
                      <span className="text-[#2563EB]">{currency((balanceSheet.liabilities || 0) + (balanceSheet.equity || 0))}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Top Expense Categories */}
          <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
            <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Top Expense Categories</h2>
            {loading ? (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
            ) : expenseBreakdown.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                <DonutChart slices={expenseBreakdown} />
                <ExpenseBreakdownList expenseBreakdown={expenseBreakdown} />
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">No expense data for selected period.</div>
            )}
          </div>
        </div>
      }
      tablesSlot={
        <div className="space-y-6">
          {/* Monthly Performance Summary */}
          <div className="rounded-xl bg-white border border-[#EEF2F7] shadow-[0_2px_8px_rgba(15,23,42,0.05)] overflow-hidden">
            <div className="px-6 py-4 border-b border-[#EEF2F7]">
              <h2 className="text-lg font-semibold text-[#1F2937]">Monthly Performance Summary</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2563EB]" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9FAFB]">
                    <tr>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">Month</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">COGS</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">Gross Profit</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">Expenses</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-[#374151] uppercase tracking-wider">Net Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {monthlyPerf.length ? (
                      monthlyPerf.map((r, idx) => (
                        <tr key={r.month} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'} hover:bg-gray-100`}>
                          <td className="px-6 py-4 text-sm font-medium text-[#1F2937]">{formatMonth(r.month)}</td>
                          <td className="px-6 py-4 text-sm text-right font-semibold text-[#16A34A]">{currency(r.total_revenue)}</td>
                          <td className="px-6 py-4 text-sm text-right font-medium text-[#1F2937]">{currency(r.cogs || r.cost_of_goods_sold || 0)}</td>
                          <td className="px-6 py-4 text-sm text-right font-semibold text-[#2563EB]">{currency(r.gross_profit)}</td>
                          <td className="px-6 py-4 text-sm text-right font-medium text-[#EF4444]">{currency(r.total_expenses)}</td>
                          <td className={`px-6 py-4 text-sm text-right font-bold ${(r.net_profit || 0) >= 0 ? 'text-[#2563EB]' : 'text-[#EF4444]'}`}>{currency(r.net_profit)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                          No data for the selected period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Quick Reports + Downloadable statements in grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Reports */}
            <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
              <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Quick Reports</h2>
              <div className="space-y-1">
                {[
                  { name: 'Income Statement', key: 'profit_loss' },
                  { name: 'Balance Sheet', key: 'balance_sheet' },
                  { name: 'Cash Flow Statement', key: 'cash_flow' },
                  { name: 'Tax Summary', key: 'expense_breakdown' },
                  { name: 'Trial Balance', key: 'monthly_performance' },
                ].map((report, idx) => (
                  <div key={report.key} className="flex items-center justify-between py-3 border-b border-[#F3F4F6] last:border-b-0">
                    <div className="flex items-center gap-3">
                      <FileText size={16} className="text-[#6B7280]" />
                      <span className="text-sm text-[#1F2937]">{report.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => exportStatementsCsv(report.key)}
                      className="text-sm text-[#2563EB] hover:underline font-medium"
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Downloadable financial statements */}
            <div className="rounded-xl bg-white border border-[#EEF2F7] p-6 shadow-[0_2px_8px_rgba(15,23,42,0.05)]">
              <h2 className="text-lg font-semibold text-[#1F2937] mb-4">Downloadable Statements</h2>
              <div className="space-y-1">
                {[
                  { name: 'Profit & Loss Statement', key: 'profit_loss' },
                  { name: 'Revenue vs Expenses', key: 'revenue_vs_expenses' },
                  { name: 'Cash Flow Statement', key: 'cash_flow' },
                  { name: 'Expense Breakdown', key: 'expense_breakdown' },
                  { name: 'Balance Sheet Summary', key: 'balance_sheet' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => exportStatementsCsv(item.key)}
                    className="w-full flex items-center justify-between px-3 py-3 rounded-lg hover:bg-[#F9FAFB] transition-colors border-b border-[#F3F4F6] last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <Download size={16} className="text-[#2563EB]" />
                      <span className="text-sm text-[#1F2937] font-medium">{item.name}</span>
                    </div>
                    <span className="text-xs text-[#2563EB] font-medium">CSV</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ReportsFinancial;